import { AppRoutes } from '../../common/constants';
import { BaseRoleplayer, ClientType, ClientValidationStatus } from '../../common/types/client';
import { Box, ButtonBase, Tooltip, Typography } from '@mui/material';
import { Icon } from '@iconify/react';
import { JSX, MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';

interface ClientStatusBadgeProps {
  client: BaseRoleplayer;
}

/**
 * Tooltip content for client status badge
 */
export const ClientStatusTooltipContent = ({ client }: ClientStatusBadgeProps): JSX.Element => {
  const navigate = useNavigate();
  let title = 'Profile not checked yet';
  switch (client.validationStatusID) {
    case ClientValidationStatus.invalid: {
      title = 'Client profile has invalid info';
      break;
    }
    case ClientValidationStatus.valid: {
      title = 'You can quote on this client';
      break;
    }
    case ClientValidationStatus.incomplete: {
      title = 'Client profile is incomplete';
      break;
    }
    case ClientValidationStatus.outOfDate: {
      title = 'Client profile is out of date';
      break;
    }
  }

  const goToEditPage = (event: MouseEvent): void => {
    event.stopPropagation();
    navigate(
      `${
        client.typeID === ClientType.Individual ? AppRoutes.NaturalPersons : AppRoutes.LegalEntities
      }/${client.id || client.roleplayerID}/edit`,
    );
  };

  return (
    <Box>
      <Typography>{title}</Typography>
      {client.validationStatusID !== ClientValidationStatus.valid && (
        <ButtonBase onClick={goToEditPage}>
          <Typography fontWeight={600}>Fix this</Typography>
        </ButtonBase>
      )}
    </Box>
  );
};

interface ClientStatusBadgeProps {
  /**
   * Client details
   */
  client: BaseRoleplayer;
  /**
   * Badge size
   * @default 14
   */
  size?: number;
  /**
   * Optional class name to be applied to the root element
   */
  className?: string;
  /**
   * Show tooltip with client status
   * @default true
   */
  showTooltip?: boolean;
}

/**
 * Render client status badge with icon and tooltip
 */
const ClientStatusBadge = ({
  client,
  className,
  showTooltip = true,
  size = 16,
}: ClientStatusBadgeProps): JSX.Element => {
  let icon = 'mdi:information-symbol';
  let color = 'warning.main';
  switch (client.validationStatusID) {
    case ClientValidationStatus.invalid: {
      color = 'error.main';
      icon = 'material-symbols:exclamation';
      break;
    }
    case ClientValidationStatus.valid: {
      color = 'success.main';
      icon = 'material-symbols:check';
      break;
    }
  }

  const content = (
    <Box
      height={size}
      width={size}
      bgcolor={color}
      color="common.white"
      className={clsx('flex items-center justify-center rounded-full', className)}
    >
      <Icon width={size} icon={icon} />
    </Box>
  );

  return showTooltip ? (
    <Tooltip
      placement="left"
      PopperProps={{ disablePortal: true }}
      title={<ClientStatusTooltipContent client={client} />}
    >
      {content}
    </Tooltip>
  ) : (
    content
  );
};

export default ClientStatusBadge;
